import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Phone, Mail, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    source: 'admin'
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Грешка при зареждане на контактите');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        interest_history: editingContact?.interest_history || [],
        assigned_broker_id: null
      };

      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, payload);
        toast.success('Контактът е обновен успешно');
      } else {
        await axios.post(`${API}/contacts`, payload);
        toast.success('Контактът е създаден успешно');
      }
      
      setIsModalOpen(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error('Грешка при запазване на контакта');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      notes: contact.notes || '',
      source: contact.source
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този контакт?')) {
      return;
    }
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success('Контактът е изтрит успешно');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Грешка при изтриване на контакта');
    }
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
      source: 'admin'
    });
  };

  const openNewModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-contacts">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
            Контакти
          </h1>
          <p className="text-muted-foreground">
            База данни с контакти на клиенти и заинтересовани лица
          </p>
        </div>
        <Button onClick={openNewModal} data-testid="add-contact-btn">
          <Plus className="w-4 h-4 mr-2" />
          Нов контакт
        </Button>
      </div>

      {/* Search */}
      <div className="admin-card mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Търсене по име, имейл или телефон..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="contact-search-input"
          />
        </div>
      </div>

      {/* Contacts Table */}
      <div className="admin-card">
        {filteredContacts.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'Няма намерени контакти' : 'Няма добавени контакти'}
            </p>
            {!searchTerm && (
              <Button className="mt-4" onClick={openNewModal}>
                <Plus className="w-4 h-4 mr-2" />
                Добави първия контакт
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Име</th>
                  <th>Имейл</th>
                  <th>Телефон</th>
                  <th>История на интерес</th>
                  <th>Източник</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td className="font-medium">
                      <button onClick={() => navigate(`/admin/contacts/${contact.id}`)}
                        className="hover:text-[#1B5E37] hover:underline text-left font-medium transition-colors">
                        {contact.name}
                      </button>
                    </td>
                    <td>
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-primary hover:underline">
                        <Mail className="w-3 h-3" />
                        {contact.email}
                      </a>
                    </td>
                    <td>
                      <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                        <Phone className="w-3 h-3" />
                        {contact.phone}
                      </a>
                    </td>
                    <td>
                      <div className="max-w-xs">
                        {contact.interest_history && contact.interest_history.length > 0 ? (
                          <ul className="text-sm text-muted-foreground">
                            {contact.interest_history.slice(-3).map((item, i) => (
                              <li key={i} className="truncate">• {item}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="px-2 py-1 bg-secondary rounded text-sm capitalize">
                        {contact.source}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(contact)}
                          data-testid={`edit-contact-${contact.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(contact.id)}
                          data-testid={`delete-contact-${contact.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground mt-4">
        Показани {filteredContacts.length} от {contacts.length} контакта
      </p>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingContact ? 'Редактиране на контакт' : 'Нов контакт'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Име *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="contact-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email">Имейл *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="contact-email-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">Телефон *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                data-testid="contact-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="notes">Бележки</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="contact-notes-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Отказ
              </Button>
              <Button type="submit" data-testid="contact-submit-btn">
                {editingContact ? 'Запази промените' : 'Създай контакт'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
